import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * Shared print machinery for every printable document in the app.
 *
 * The pattern this generalises was written for the cari ekstre and is now used
 * by three documents (ekstre, satınalma siparişi, sipariş formu). All of them
 * need the same four things, and each of the four is a bug if it is left out:
 *
 *   1. **Snapshot, then portal.** The printable markup is built from a snapshot
 *      taken when the button was clicked, never from live query state, and it is
 *      rendered through a portal straight onto `<body>` — a SIBLING of `#root`,
 *      not a descendant. That is what lets a print stylesheet hide the entire
 *      app shell with `#root { display: none }` instead of the `visibility:
 *      hidden` trick, which leaves blank pages behind from invisible layout.
 *   2. **A body class, not a bare `@media print`.** The class is toggled only
 *      for the moment the dialog is open, so a native Ctrl+P on any other route
 *      (where no printable document exists) still prints the screen instead of
 *      blanking it.
 *   3. **`afterprint` resets everything.** The dialog closing — printed or
 *      cancelled — takes the app shell back and retires the snapshot, so a
 *      document that can run to thousands of rows does not sit in the DOM for
 *      as long as the screen stays open.
 *   4. **Supersession + abort.** A second click, a navigation to another
 *      record, or an unmount must be able to retire a run that is still in
 *      flight; otherwise a finished fetch prints the record the user has
 *      already navigated away from, under the new record's identity.
 *
 * What stays with the caller: the document's own markup, its CSS class, and the
 * `load` function that produces the snapshot. A document whose data is already
 * on screen passes a `load` that just returns it — the snapshot semantics and
 * the printing are worth having even when nothing is fetched.
 */

/* ── Job state ──────────────────────────────────────────────────────────────── */

export type PrintJobState<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'error' }

export type PrintJob<T> = PrintJobState<T> & {
  /** Starts a run. Safe to call while one is in flight — it supersedes it. */
  run: () => void
}

export interface PrintJobOptions<T> {
  /**
   * Class toggled on `<body>` while the print dialog is open. The print
   * stylesheet hides `#root` and shows the portal under exactly this class, so
   * it has to match the CSS (`print-statement`, `print-document`, …).
   */
  bodyClass: string
  /**
   * Identity of the record being printed. Changing it retires an in-flight run
   * and clears any snapshot: a detail screen is NOT remounted when its route
   * param changes, so without this a print started for record A would land —
   * and open the dialog — after the user has already moved to record B.
   */
  resetKey: string
  /** Builds the snapshot to print. Must honour `signal` if it does any I/O. */
  load: (signal: AbortSignal) => Promise<T>
  /**
   * False while the caller's data cannot be trusted to belong to `resetKey` —
   * TanStack's `keepPreviousData` window, most importantly. `run` refuses to
   * start rather than print two records stapled together.
   */
  enabled?: boolean
  /**
   * Last check before the dialog opens: false leaves the snapshot in place
   * (harmless — the portal is invisible on screen) but does not print it.
   * Defaults to always printable.
   */
  canPrint?: (data: T) => boolean
}

/**
 * Drives one printable document: snapshot → portal → dialog → reset.
 *
 * The returned state doubles as the render gate — `job.status === 'ready'`
 * narrows `job.data` to the snapshot the portal should render.
 */
export function usePrintJob<T>({
  bodyClass,
  resetKey,
  load,
  enabled = true,
  canPrint,
}: PrintJobOptions<T>): PrintJob<T> {
  const [state, setState] = useState<PrintJobState<T>>({ status: 'idle' })
  // Bumped by every `run()` call AND by a record change or unmount, so a
  // finished load can tell whether anybody still wants its result.
  const requestId = useRef(0)
  // Aborts the I/O of whichever run the token has moved past — without it a
  // superseded print keeps pulling data to nowhere.
  const abortRef = useRef<AbortController | null>(null)
  /*
   * Decided by `run` at the moment the snapshot lands, read by the print effect
   * below. Deliberately NOT part of the state object: `canPrint` is typically
   * an inline arrow, so putting it in the effect's dependencies would re-run
   * the effect — and re-open the print dialog — on every render.
   */
  const printableRef = useRef(true)

  /**
   * Retires the run in flight, if any, and hands back the token of the run that
   * replaces it. The two halves belong together: the token stops a finished
   * load from writing state, the abort stops the load from continuing.
   */
  const supersede = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    requestId.current += 1
    return requestId.current
  }, [])

  // A detail screen stays mounted across a `/x/$a` → `/x/$b` navigation; the
  // cleanup covers unmount for free, since nothing that comes back after
  // either event is wanted.
  useEffect(() => {
    setState({ status: 'idle' })
    return () => {
      supersede()
    }
  }, [resetKey, supersede])

  useEffect(() => {
    if (state.status !== 'ready' || !printableRef.current) return
    document.body.classList.add(bodyClass)
    // `afterprint` fires whether the dialog was used or cancelled. Either way
    // the app shell comes back AND the snapshot is retired, so the document
    // stops occupying the DOM. The cost is that a second click re-runs `load` —
    // rare, and it guarantees the second copy is freshly built.
    const finish = () => {
      document.body.classList.remove(bodyClass)
      setState({ status: 'idle' })
    }
    window.addEventListener('afterprint', finish, { once: true })
    // One frame so the (until now unmounted) document paints before the browser
    // paginates it for print.
    const frame = requestAnimationFrame(() => window.print())
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('afterprint', finish)
      document.body.classList.remove(bodyClass)
    }
  }, [state, bodyClass])

  function run(): void {
    if (!enabled) return
    const thisRequest = supersede()
    const controller = new AbortController()
    abortRef.current = controller
    setState({ status: 'loading' })
    void load(controller.signal)
      .then((data) => {
        if (requestId.current !== thisRequest) return // superseded — discard
        printableRef.current = canPrint ? canPrint(data) : true
        setState({ status: 'ready', data })
      })
      .catch(() => {
        // A superseded or unmounted run rejects with its own abort — never an
        // error the user should see, and on unmount there is nobody to see it.
        if (requestId.current !== thisRequest) return
        setState({ status: 'error' })
      })
  }

  return { ...state, run }
}

/* ── Printed-on stamp ───────────────────────────────────────────────────────── */

const istanbulDate = new Intl.DateTimeFormat('tr-TR', {
  timeZone: 'Europe/Istanbul',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

/**
 * Today by factory clock as `dd.mm.yyyy`, for a document's "printed on" footer.
 *
 * Reads the WORKSTATION's clock and reprojects it onto Europe/Istanbul, which
 * fixes a browser in the wrong timezone but not a machine whose system date is
 * itself wrong. Acceptable here and only here: the stamp says when the paper
 * came out of the printer, not anything about the record. Every figure on the
 * documents comes from the API, where dates are the factory clock by
 * construction.
 */
export function printedOn(): string {
  return istanbulDate.format(new Date())
}

/* ── Portal ─────────────────────────────────────────────────────────────────── */

/**
 * Renders a printable document onto `<body>`, outside `#root`.
 *
 * `className` is the document's print class (`print-only`, `print-doc`); it is
 * `display: none` outside `@media print` in every stylesheet that defines one,
 * so this never affects the screen layout.
 */
export function PrintPortal({
  className,
  children,
}: {
  className: string
  children: ReactNode
}) {
  return createPortal(<div className={className}>{children}</div>, document.body)
}
